import { Table } from '../ui/Table';
import { Button } from '../ui/Button';
import { RefreshCw, BarChart3 } from 'lucide-react';
import { useCurrency } from '../../hooks/useCurrency';
import type { AnalyticReportData, AnalyticReportAccountData } from '../../types/analytic-accounting';
import { ANALYTIC_ACCOUNT_TYPE_LABELS, ANALYTIC_ACCOUNT_TYPE_COLORS } from '../../types/analytic-accounting';

interface AnalyticReportViewProps {
  data: AnalyticReportData | null;
  loading: boolean;
  onRefresh: () => void;
}

export function AnalyticReportView({ data, loading, onRefresh }: AnalyticReportViewProps) {
  const { formatConverted } = useCurrency();

  const columns = [
    {
      header: 'Code',
      accessor: (row: AnalyticReportAccountData) => (
        <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
          {row.code}
        </span>
      ),
    },
    {
      header: 'Compte',
      accessor: (row: AnalyticReportAccountData) => row.name,
    },
    {
      header: 'Type',
      accessor: (row: AnalyticReportAccountData) => {
        const colors = ANALYTIC_ACCOUNT_TYPE_COLORS[row.type];
        return (
          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
            {ANALYTIC_ACCOUNT_TYPE_LABELS[row.type]}
          </span>
        );
      },
    },
    {
      header: 'Débit',
      accessor: (row: AnalyticReportAccountData) => formatConverted(row.totalDebit),
      className: 'text-right',
    },
    {
      header: 'Crédit',
      accessor: (row: AnalyticReportAccountData) => formatConverted(row.totalCredit),
      className: 'text-right',
    },
    {
      header: 'Solde',
      accessor: (row: AnalyticReportAccountData) => (
        <span className={`font-semibold ${row.balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {formatConverted(Math.abs(row.balance))} {row.balance < 0 ? '(C)' : '(D)'}
        </span>
      ),
      className: 'text-right',
    },
    {
      header: 'Écritures',
      accessor: (row: AnalyticReportAccountData) => row.lineCount,
      className: 'text-right',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Rapport par compte analytique</h3>
        </div>
        <Button variant="ghost" size="sm" icon={RefreshCw} onClick={onRefresh} isLoading={loading}>
          Actualiser
        </Button>
      </div>

      {data && data.totals && (
        <div className="grid grid-cols-3 gap-4">
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs text-muted-foreground">Total débit</p>
            <p className="text-lg font-semibold text-foreground">{formatConverted(data.totals.totalDebit)}</p>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs text-muted-foreground">Total crédit</p>
            <p className="text-lg font-semibold text-foreground">{formatConverted(data.totals.totalCredit)}</p>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs text-muted-foreground">Comptes ventilés</p>
            <p className="text-lg font-semibold text-foreground">{data.totals.accountCount}</p>
          </div>
        </div>
      )}

      <Table
        columns={columns}
        data={data?.accounts || []}
        loading={loading}
        emptyTitle="Aucune donnée"
        emptyMessage="Aucune ventilation analytique n'a été enregistrée pour cet exercice"
      />
    </div>
  );
}
